import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CalendarBlank,
  CaretLeft,
  CheckCircle,
  Copy,
  CurrencyInr,
  Envelope,
  IdentificationCard,
  Lock,
  MapPin,
  ShieldCheck,
  SpinnerGap
} from '@phosphor-icons/react';
import { internshipBySlug, internshipCover, internshipCategoryLabel, DEFAULT_INTERNSHIP_PLANS } from '../../data/internships';
import { internshipAPI } from '../../services/api';
import { applyPageSeo } from '../../utils/seo';

const STEPS = ['Details', 'Payment', 'Your ID'];
const EMAIL_RE = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
const PHONE_RE = /^[6-9]\d{9}$/;

const RAZORPAY_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) {
    resolve(window.Razorpay);
    return;
  }

  const finish = () => {
    if (window.Razorpay) resolve(window.Razorpay);
    else reject(new Error('Razorpay did not load.'));
  };

  const existing = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
  if (existing) {
    const wait = window.setInterval(() => {
      if (window.Razorpay) {
        window.clearInterval(wait);
        resolve(window.Razorpay);
      }
    }, 80);
    window.setTimeout(() => {
      window.clearInterval(wait);
      finish();
    }, 12000);
    return;
  }

  const script = document.createElement('script');
  script.src = RAZORPAY_SRC;
  script.async = true;
  const timer = window.setTimeout(() => reject(new Error('Razorpay checkout timed out. Check your connection and try again.')), 12000);
  script.onload = () => {
    window.clearTimeout(timer);
    finish();
  };
  script.onerror = () => {
    window.clearTimeout(timer);
    reject(new Error('Could not load Razorpay checkout.'));
  };
  document.body.appendChild(script);
});

const InternshipApply = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const role = useMemo(() => internshipBySlug(slug), [slug]);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payMeta, setPayMeta] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState('');
  const [plans, setPlans] = useState(DEFAULT_INTERNSHIP_PLANS);
  const [planId, setPlanId] = useState(params.get('plan') || '');
  const [form, setForm] = useState({
    name: '',
    college: '',
    date_of_birth: '',
    email: '',
    phone: '',
    coupon_code: ''
  });
  const [quote, setQuote] = useState(null);
  const [couponNote, setCouponNote] = useState('');
  const [rzOrder, setRzOrder] = useState(null);
  const [preparingPay, setPreparingPay] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    internshipAPI.catalog()
      .then(({ data }) => {
        const next = data.plans?.length ? data.plans : DEFAULT_INTERNSHIP_PLANS;
        setPlans(next);
        setPlanId((prev) => prev || String(next[0]?.id || ''));
      })
      .catch(() => {});
    return applyPageSeo({
      title: role
        ? `Apply ${role.title} | Paid Internship Satara | Growmore IT Services`
        : 'Apply Internship | Growmore IT Services',
      description: role
        ? `Apply for the ${role.headline || role.title} at Growmore IT Services, Satara.`
        : 'Apply for a paid internship in Satara.',
      keywords: role?.seoKeywords,
      path: role ? `/internship/apply/${role.slug}` : '/internship',
      robots: 'noindex,nofollow'
    });
  }, [role]);

  const plan = plans.find((item) => String(item.id) === String(planId)) || plans[0];
  const listedPrice = Number(plan?.price || 0);
  const fee = quote?.fee ?? listedPrice;
  const cover = role ? internshipCover(role.slug) : '';

  useEffect(() => {
    setQuote(null);
    setCouponNote('');
  }, [planId]);

  useEffect(() => {
    loadRazorpay().catch(() => {});
  }, []);

  useEffect(() => {
    if (step !== 2 || !payMeta?.application_id) return undefined;

    let cancelled = false;
    setPreparingPay(true);
    setRzOrder(null);

    Promise.all([
      loadRazorpay(),
      internshipAPI.createOrder({ application_id: payMeta.application_id })
    ])
      .then(([, res]) => {
        if (cancelled) return;
        setRzOrder(res.data);
        setError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setRzOrder(null);
        setError(err.response?.data?.message || err.message || 'Could not prepare Razorpay checkout.');
      })
      .finally(() => {
        if (!cancelled) setPreparingPay(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, payMeta]);

  if (!role) {
    return (
      <div className="pt-32 pb-20 text-center min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
        <p className="text-gray-500 dark:text-gray-400 mb-4">This internship was not found.</p>
        <Link to="/internship" className="text-brand-600 dark:text-brand-400 font-semibold">Back to internships</Link>
      </div>
    );
  }

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      let digits = value.replace(/\D/g, '');
      if (digits.startsWith('91') && digits.length >= 12) {
        digits = digits.slice(-10);
      }
      setForm((prev) => ({ ...prev, phone: digits.slice(0, 10) }));
      return;
    }
    if (name === 'email') {
      setForm((prev) => ({ ...prev, email: value.replace(/\s/g, '').toLowerCase() }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const phoneOk = PHONE_RE.test(form.phone);
  const emailOk = EMAIL_RE.test(form.email);
  const detailsReady = Boolean(form.name.trim() && form.college.trim() && form.date_of_birth && phoneOk && emailOk);

  const copyText = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(label);
      window.setTimeout(() => setCopied(''), 1600);
    } catch {
      setCopied('');
    }
  };

  const applyCoupon = async () => {
    if (!plan || !form.coupon_code.trim()) return;
    try {
      const { data } = await internshipAPI.quoteCoupon({ plan_id: plan.id, role_slug: role.slug, code: form.coupon_code });
      setQuote(data);
      setCouponNote(`Saved ₹${Number(data.discount).toLocaleString('en-IN')}`);
      setError('');
    } catch (err) {
      setQuote(null);
      setCouponNote('');
      setError(err.response?.data?.message || 'This coupon is not valid.');
    }
  };

  const submitDetails = async (e) => {
    e.preventDefault();
    if (!plan) {
      setError('Select a plan.');
      return;
    }
    if (!phoneOk) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    if (!emailOk) {
      setError('Enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { data } = await internshipAPI.apply({
        name: form.name,
        college: form.college,
        date_of_birth: form.date_of_birth,
        email: form.email,
        phone: form.phone,
        role_slug: role.slug,
        plan_id: plan.id,
        coupon_code: form.coupon_code || undefined
      });
      setPayMeta(data);
      setStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save details.');
    } finally {
      setLoading(false);
    }
  };

  const finishPaid = (data) => {
    setCredentials(data);
    setStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openRazorpay = (order) => {
    const RazorpayCheckout = window.Razorpay;
    if (!RazorpayCheckout) {
      setLoading(false);
      setError('Razorpay checkout is not ready. Refresh the page and try again.');
      return;
    }

    const key = order.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;
    if (!key) {
      setLoading(false);
      setError('Razorpay key is missing. Restart the frontend after adding REACT_APP_RAZORPAY_KEY_ID.');
      return;
    }

    const rzp = new RazorpayCheckout({
      key,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: 'Growmore IT Services',
      description: `${payMeta.role_title || role.title} · ${payMeta.plan_name}`,
      image: `${window.location.origin}/logo-full.png`,
      order_id: order.order_id,
      prefill: {
        name: form.name,
        email: form.email,
        contact: form.phone
      },
      notes: {
        intern_id: String(payMeta.application_id),
        company: 'Growmore IT Services'
      },
      theme: { color: '#0284c7' },
      modal: {
        ondismiss: () => {
          setLoading(false);
          setError('Payment cancelled. You can try again when ready.');
        }
      },
      handler: async (response) => {
        try {
          setLoading(true);
          setError('');
          const { data } = await internshipAPI.verifyPayment({
            application_id: payMeta.application_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          });
          finishPaid(data);
        } catch (err) {
          setError(err.response?.data?.message || 'Payment verification failed.');
        } finally {
          setLoading(false);
        }
      }
    });

    rzp.on('payment.failed', (resp) => {
      setLoading(false);
      setError(resp?.error?.description || 'Payment failed. Please try again.');
    });

    setLoading(false);
    rzp.open();
  };

  const payWithRazorpay = () => {
    if (!payMeta?.application_id) return;
    setError('');

    if (preparingPay || !rzOrder) {
      setError('Checkout is still preparing. Please wait a moment.');
      return;
    }

    if (rzOrder.free) {
      setLoading(true);
      internshipAPI.verifyPayment({
        application_id: payMeta.application_id,
        free: true
      })
        .then(({ data }) => finishPaid(data))
        .catch((err) => {
          setError(err.response?.data?.message || 'Could not activate this internship.');
        })
        .finally(() => setLoading(false));
      return;
    }

    if (!window.Razorpay) {
      setLoading(true);
      loadRazorpay()
        .then(() => openRazorpay(rzOrder))
        .catch((err) => {
          setLoading(false);
          setError(err.message || 'Could not load Razorpay checkout.');
        });
      return;
    }

    openRazorpay(rzOrder);
  };

  const retryPreparePay = () => {
    if (!payMeta?.application_id) return;
    setError('');
    setPreparingPay(true);
    setRzOrder(null);
    Promise.all([
      loadRazorpay(),
      internshipAPI.createOrder({ application_id: payMeta.application_id })
    ])
      .then(([, res]) => {
        setRzOrder(res.data);
        setError('');
      })
      .catch((err) => {
        setRzOrder(null);
        setError(err.response?.data?.message || err.message || 'Could not prepare Razorpay checkout.');
      })
      .finally(() => setPreparingPay(false));
  };

  const payAmount = Number(payMeta?.fee || fee);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50 dark:from-dark-900 dark:via-dark-900 dark:to-dark-800">
      <div className="absolute -top-16 right-10 w-64 h-64 bg-brand-400/15 rounded-full blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute bottom-24 left-8 w-40 h-40 bg-violet-400/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative pt-28 pb-20">
        <div className="section-max-width px-4 md:px-6">
          <button
            type="button"
            onClick={() => navigate(`/internship/${role.slug}`)}
            className="text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 inline-flex items-center gap-1 mb-6"
          >
            <CaretLeft className="w-4 h-4" /> Back to role
          </button>

          <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-start">
            <aside className="md:col-span-4 md:sticky md:top-28 space-y-4">
              <div className="rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-800 bg-white dark:bg-dark-800 shadow-soft">
                <div className="relative h-40">
                  <img src={cover} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-slate-900/10" />
                  <p className="absolute bottom-3 left-4 text-[11px] font-bold uppercase tracking-[0.16em] text-sky-200">
                    {internshipCategoryLabel(role.category)} intern
                  </p>
                </div>
                <div className="p-5">
                  <h1 className="font-display text-2xl font-extrabold text-gray-900 dark:text-white leading-tight">{role.title}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-6 mt-2">{role.summary}</p>
                  <div className="mt-4 rounded-2xl bg-slate-50 dark:bg-white/5 border border-gray-100 dark:border-gray-700 p-4 space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{plan ? plan.name : 'Select a plan'}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 inline-flex items-center gap-1.5">
                      <CalendarBlank className="w-4 h-4 text-brand-600" /> {plan ? `${plan.weeks} weeks` : '—'}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 inline-flex items-center gap-1.5">
                      <CurrencyInr className="w-4 h-4 text-brand-600" /> ₹{fee.toLocaleString('en-IN')}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 inline-flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-brand-600" /> Satara
                    </p>
                  </div>
                </div>
              </div>

              <ol className="rounded-3xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-dark-800 shadow-soft p-5 space-y-3">
                {STEPS.map((label, i) => (
                  <li key={label} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 w-7 h-7 rounded-lg text-xs font-bold flex items-center justify-center shrink-0 ${
                        step === i + 1
                          ? 'bg-brand-600 text-white'
                          : step > i + 1
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300'
                            : 'bg-slate-100 dark:bg-white/10 text-gray-500 dark:text-gray-400'
                      }`}
                    >
                      {step > i + 1 ? '✓' : i + 1}
                    </span>
                    <div>
                      <p className={`text-sm font-semibold ${step === i + 1 ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-300'}`}>{label}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {i === 0 && 'Your name, college and contact details.'}
                        {i === 1 && 'Pay securely with Razorpay (UPI, card, netbanking).'}
                        {i === 2 && 'Your intern ID and password appear here.'}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </aside>

            <div className="md:col-span-8 min-w-0">
              <div className="flex flex-wrap gap-2 mb-6">
                {STEPS.map((label, i) => (
                  <span
                    key={label}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                      step === i + 1
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-white dark:bg-dark-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {i + 1}. {label}
                  </span>
                ))}
              </div>

              {error && (
                <p className="mb-4 text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950/40 border border-red-100 dark:border-red-900 rounded-xl px-4 py-3">
                  {error}
                </p>
              )}

              {step === 1 && (
                <form onSubmit={submitDetails} className="rounded-3xl bg-white/90 dark:bg-dark-800/90 border border-gray-100 dark:border-gray-800 shadow-soft p-5 sm:p-8 space-y-5">
                  <div>
                    <h2 className="font-display text-2xl font-extrabold text-gray-900 dark:text-white">Application details</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-6">
                      Open to everyone. Weekly tasks are small and simple. Fill the form, then complete payment.
                    </p>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Plan</label>
                      <select required className="input-field dark:[color-scheme:dark]" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                        {plans.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.name} — ₹{Number(item.price).toLocaleString('en-IN')} ({item.weeks} weeks)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Coupon code (optional)</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          name="coupon_code"
                          className="input-field uppercase"
                          placeholder="SUMMER20"
                          value={form.coupon_code}
                          onChange={onChange}
                        />
                        <button
                          type="button"
                          className="px-5 py-3 rounded-xl bg-slate-100 dark:bg-white/10 text-gray-800 dark:text-white font-semibold text-sm whitespace-nowrap"
                          onClick={applyCoupon}
                        >
                          Apply
                        </button>
                      </div>
                      {couponNote && (
                        <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 mt-2">
                          {couponNote} · pay ₹{fee.toLocaleString('en-IN')}
                        </p>
                      )}
                    </div>

                    {[
                      { name: 'name', label: 'Full name', type: 'text', span: '' },
                      { name: 'college', label: 'College / university', type: 'text', span: '' },
                      { name: 'date_of_birth', label: 'Date of birth', type: 'date', span: '' }
                    ].map((field) => (
                      <div key={field.name} className={field.span}>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">{field.label}</label>
                        <input
                          required
                          name={field.name}
                          type={field.type}
                          value={form[field.name]}
                          onChange={onChange}
                          className="input-field dark:[color-scheme:dark]"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Mobile number</label>
                      <input
                        required
                        name="phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        maxLength={10}
                        pattern="[6-9][0-9]{9}"
                        placeholder="10-digit mobile"
                        value={form.phone}
                        onChange={onChange}
                        className="input-field dark:[color-scheme:dark]"
                      />
                      <p className={`mt-1 text-xs ${form.phone && !phoneOk ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                        {form.phone && !phoneOk ? 'Enter a valid 10-digit Indian mobile number.' : 'Exactly 10 digits. Do not add +91.'}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1.5">Email</label>
                      <input
                        required
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="name@email.com"
                        value={form.email}
                        onChange={onChange}
                        className="input-field dark:[color-scheme:dark]"
                      />
                      <p className={`mt-1 text-xs ${form.email && !emailOk ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'}`}>
                        {form.email && !emailOk
                          ? 'Enter a valid email address.'
                          : 'Intern ID, password, welcome mail and offer letter go to this email from info@growmoreitservices.com.'}
                      </p>
                    </div>
                  </div>

                  <button type="submit" disabled={loading || !detailsReady} className="btn-primary text-white w-full inline-flex justify-center gap-2">
                    {loading ? <SpinnerGap className="w-5 h-5 animate-spin" /> : 'Continue to payment'}
                  </button>
                </form>
              )}

              {step === 2 && payMeta && (
                <div className="rounded-3xl bg-white/90 dark:bg-dark-800/90 border border-gray-100 dark:border-gray-800 shadow-soft p-5 sm:p-8 space-y-5">
                  <div>
                    <h2 className="font-display text-2xl font-extrabold text-gray-900 dark:text-white">Complete payment</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-6">
                      Pay securely with Razorpay. UPI, cards and netbanking open in the checkout window.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-800 p-5">
                    <p className="text-xs font-bold uppercase tracking-wide text-brand-700 dark:text-brand-300">Amount to pay</p>
                    <p className="font-display text-4xl font-extrabold text-gray-900 dark:text-white mt-1 inline-flex items-center">
                      <CurrencyInr className="w-8 h-8" /> {payAmount.toLocaleString('en-IN')}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
                      {payMeta.plan_name} · {payMeta.weeks} weeks · {payMeta.role_title || role.title}
                    </p>
                    <p className="mt-3 text-xs font-semibold text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-brand-600" weight="fill" />
                      Encrypted checkout · activated only after signature verify
                    </p>
                  </div>

                  <button
                    type="button"
                    disabled={loading || preparingPay || !rzOrder}
                    onClick={payWithRazorpay}
                    className="btn-primary text-white w-full inline-flex justify-center items-center gap-2 py-3.5"
                  >
                    {loading || preparingPay ? <SpinnerGap className="w-5 h-5 animate-spin" /> : (
                      <>
                        <Lock className="w-4 h-4" weight="bold" />
                        {payAmount > 0 ? `Pay ₹${payAmount.toLocaleString('en-IN')} with Razorpay` : 'Activate internship'}
                      </>
                    )}
                  </button>
                  {preparingPay && (
                    <p className="text-xs text-center text-gray-500 dark:text-gray-400">Preparing secure Razorpay checkout…</p>
                  )}
                  {!preparingPay && !rzOrder && (
                    <button
                      type="button"
                      onClick={retryPreparePay}
                      className="text-sm font-semibold text-brand-600 dark:text-brand-400 w-full"
                    >
                      Retry checkout setup
                    </button>
                  )}
                </div>
              )}

              {step === 3 && credentials && (
                <div className="rounded-3xl bg-white/90 dark:bg-dark-800/90 border border-gray-100 dark:border-gray-800 shadow-soft p-5 sm:p-8">
                  <CheckCircle className="w-12 h-12 text-emerald-600 dark:text-emerald-400 mb-3" weight="fill" />
                  <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white mb-2">Internship activated</h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 leading-7 mb-6 max-w-xl">
                    Save these details now. We also emailed your Intern ID, password, welcome note and offer letter from info@growmoreitservices.com. Keep them safe — you will use them to sign in.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {[
                      { key: 'id', label: 'Intern ID', value: credentials.intern_code, icon: IdentificationCard },
                      { key: 'email', label: 'Email', value: credentials.email || form.email, icon: Envelope },
                      { key: 'password', label: 'Password', value: credentials.password, icon: Lock, span: 'sm:col-span-2' }
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <div
                          key={item.key}
                          className={`rounded-2xl bg-slate-50 dark:bg-white/5 border border-gray-100 dark:border-gray-700 p-4 ${item.span || ''}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 inline-flex items-center gap-1.5">
                                <Icon className="w-3.5 h-3.5" /> {item.label}
                              </p>
                              <p className="mt-1 font-mono text-base sm:text-lg font-bold text-gray-900 dark:text-white break-all">{item.value || '—'}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => copyText(item.key, item.value)}
                              className="shrink-0 p-2 rounded-lg text-gray-400 hover:text-brand-600 dark:hover:text-brand-300 hover:bg-white dark:hover:bg-white/10"
                              aria-label={`Copy ${item.label}`}
                            >
                              {copied === item.key ? <CheckCircle className="w-5 h-5 text-emerald-600" weight="fill" /> : <Copy className="w-5 h-5" />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {credentials.offer_letter_code && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                      Offer letter:{' '}
                      <Link className="text-brand-600 dark:text-brand-400 font-semibold" to={`/verify/${credentials.offer_letter_code}`}>
                        {credentials.offer_letter_code}
                      </Link>
                    </p>
                  )}

                  <Link to="/intern/login" className="btn-primary text-white inline-flex items-center justify-center">
                    Open intern login
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InternshipApply;
